import React, { useState, useEffect } from 'react';
import { Alert, Row, Col } from 'react-bootstrap';
import Button from './Button';
import { useActualites } from '../context/ActualitesContext';
import '../styles/components.css';

const ActualiteForm = ({ actualite, onClose }) => {
  const { addActualite, updateActualite } = useActualites();
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    location: '',
    description: '',
    image: '',
    link: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (actualite) {
      setFormData({
        title: actualite.title || '',
        date: actualite.date || '',
        location: actualite.location || '',
        description: actualite.description || '',
        image: actualite.image || '',
        link: actualite.link || ''
      });
    }
  }, [actualite]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Veuillez sélectionner une image (JPG, PNG, GIF, WebP)' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({ ...prev, image: reader.result }));
      if (errors.image) setErrors(prev => ({ ...prev, image: undefined }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }
    
    if (!formData.date.trim()) {
      newErrors.date = 'La date est requise';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'La description doit contenir au moins 20 caractères';
    }
    
    // Image: soit URL valide, soit data URL (upload)
    if (formData.image && !formData.image.startsWith('data:') && !isValidUrl(formData.image)) {
      newErrors.image = 'URL invalide';
    }
    
    if (formData.link && !isValidUrl(formData.link)) {
      newErrors.link = 'URL invalide';
    }
    
    return newErrors;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      if (actualite) {
        updateActualite(actualite.id, formData);
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        addActualite(formData);
        setSubmitStatus('success');
        setFormData({
          title: '',
          date: '',
          location: '',
          description: '',
          image: '',
          link: ''
        });
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {submitStatus === 'success' && (
        <Alert variant="success" className="mb-4">
          {actualite ? 'Actualité modifiée avec succès !' : 'Actualité ajoutée avec succès !'}
        </Alert>
      )}
      
      {submitStatus === 'error' && (
        <Alert variant="danger" className="mb-4">
          Une erreur est survenue. Veuillez réessayer.
        </Alert>
      )}

      <div className="form-group-modern">
        <label htmlFor="title" className="form-label-modern">
          Titre <span style={{ color: 'var(--color-error)' }}>*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className={`form-control-modern ${errors.title ? 'is-invalid' : ''}`}
          placeholder="Titre de l'actualité"
          required
        />
        {errors.title && <div className="form-error">{errors.title}</div>}
      </div>

      <Row>
        <Col md={6}>
          <div className="form-group-modern">
            <label htmlFor="date" className="form-label-modern">
              Date <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="text"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={`form-control-modern ${errors.date ? 'is-invalid' : ''}`}
              placeholder="Ex: 4-7 octobre 2025"
              required
            />
            {errors.date && <div className="form-error">{errors.date}</div>}
          </div>
        </Col>
        <Col md={6}>
          <div className="form-group-modern">
            <label htmlFor="location" className="form-label-modern">
              Localisation <span style={{ color: 'var(--color-text-muted)' }}>(optionnel)</span>
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="form-control-modern"
              placeholder="Ex: Lyon, France"
            />
          </div>
        </Col>
      </Row>

      <div className="form-group-modern">
        <label htmlFor="description" className="form-label-modern">
          Description <span style={{ color: 'var(--color-error)' }}>*</span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className={`form-control-modern ${errors.description ? 'is-invalid' : ''}`}
          placeholder="Description de l'actualité..."
          rows="5"
          required
        />
        {errors.description && <div className="form-error">{errors.description}</div>}
        <small style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {formData.description.length} caractères (minimum 20)
        </small>
      </div>

      <div className="form-group-modern">
        <label className="form-label-modern">
          Image <span style={{ color: 'var(--color-text-muted)' }}>(optionnel)</span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-md)', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 200px' }}>
            <input
              type="url"
              id="image"
              name="image"
              value={formData.image?.startsWith('data:') ? '' : (formData.image || '')}
              onChange={handleChange}
              className={`form-control-modern ${errors.image ? 'is-invalid' : ''}`}
              placeholder="URL de l'image (ex: Unsplash)"
            />
          </div>
          <div style={{ flex: '0 0 auto' }}>
            <label
              htmlFor="image-upload"
              style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--border-radius-lg)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text)'
              }}
            >
              Ou télécharger depuis le PC
            </label>
            <input
              type="file"
              id="image-upload"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        {formData.image && formData.image.startsWith('data:') && (
          <div style={{ marginTop: 'var(--spacing-sm)' }}>
            <img
              src={formData.image}
              alt="Aperçu"
              style={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: 'var(--border-radius-md)' }}
            />
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
              style={{
                marginLeft: 'var(--spacing-sm)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-error)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Supprimer l'image
            </button>
          </div>
        )}
        {errors.image && <div className="form-error">{errors.image}</div>}
        <small style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', display: 'block', marginTop: '0.25rem' }}>
          URL ou fichier depuis votre ordinateur (JPG, PNG, GIF, WebP)
        </small>
      </div>

      <div className="form-group-modern">
        <label htmlFor="link" className="form-label-modern">
          Lien externe <span style={{ color: 'var(--color-text-muted)' }}>(optionnel)</span>
        </label>
        <input
          type="url"
          id="link"
          name="link"
          value={formData.link}
          onChange={handleChange}
          className={`form-control-modern ${errors.link ? 'is-invalid' : ''}`}
          placeholder="https://exemple.com/article"
        />
        {errors.link && <div className="form-error">{errors.link}</div>}
      </div>

      <div style={{ 
        display: 'flex', 
        gap: 'var(--spacing-md)', 
        justifyContent: 'flex-end',
        marginTop: 'var(--spacing-xl)',
        paddingTop: 'var(--spacing-lg)',
        borderTop: '1px solid var(--color-border)'
      }}>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting 
            ? 'Enregistrement...' 
            : actualite 
              ? 'Modifier' 
              : 'Ajouter'}
        </Button>
      </div>
    </form>
  );
};

export default ActualiteForm;
